// Tour anchors: pages mark controls with data-tour="<id>" (via tourAnchor). These helpers find them.
// No layout calls here (no getBoundingClientRect or offset reads), so a check is cheap and safe in observers.

export const tourAnchor = (id: string): { readonly "data-tour": string } => ({ "data-tour": id });

const ATTR = "data-tour";

// CSS.escape where it exists; otherwise escape the characters that can break a quoted attribute selector.
function escapeId(id: string): string {
  const css = (globalThis as { CSS?: { escape?: (value: string) => string } }).CSS;
  if (css && typeof css.escape === "function") return css.escape(id);
  return id.replace(/["\\.]/g, "\\$&");
}

// data-tour ids of the target and its ancestors, nearest first.
export function anchorsOf(target: Element | null): string[] {
  const ids: string[] = [];
  for (let el: Element | null = target; el; el = el.parentElement) {
    const id = el.getAttribute(ATTR);
    if (id) ids.push(id);
  }
  return ids;
}

// Connected, not hidden by display, visibility, the hidden attribute, inert, or aria-hidden on an ancestor.
export function isRendered(el: Element): boolean {
  if (!el.isConnected) return false;
  if (el.closest("[hidden],[inert],[aria-hidden='true']")) return false;

  const view = el.ownerDocument.defaultView;
  if (!view || typeof view.getComputedStyle !== "function") return true;
  if (view.getComputedStyle(el).visibility === "hidden") return false;
  for (let node: Element | null = el; node; node = node.parentElement) {
    if (view.getComputedStyle(node).display === "none") return false;
  }
  return true;
}

// First rendered element with this anchor id.
export function findAnchor(id: string, root: ParentNode = globalThis.document): HTMLElement | null {
  const matches = root.querySelectorAll<HTMLElement>(`[${ATTR}="${escapeId(id)}"]`);
  for (const el of matches) {
    if (isRendered(el)) return el;
  }
  return null;
}

// Every anchor id with at least one rendered element under root.
export function presentAnchors(root: ParentNode = globalThis.document): Set<string> {
  const present = new Set<string>();
  for (const el of root.querySelectorAll<HTMLElement>(`[${ATTR}]`)) {
    const id = el.getAttribute(ATTR);
    if (id && !present.has(id) && isRendered(el)) present.add(id);
  }
  return present;
}

// Calls cb once now, then at most once per animation frame after a DOM change to data-tour anchors.
// The returned function disconnects the observer and cancels any pending frame.
export function watchAnchors(cb: (present: Set<string>) => void, root: Document = globalThis.document): () => void {
  const g = globalThis as {
    requestAnimationFrame?: (fn: () => void) => number;
    cancelAnimationFrame?: (id: number) => void;
  };
  const raf = g.requestAnimationFrame ?? ((fn: () => void) => setTimeout(fn, 16) as unknown as number);
  const caf = g.cancelAnimationFrame ?? ((id: number) => clearTimeout(id as unknown as ReturnType<typeof setTimeout>));

  let frame: number | null = null;
  let disconnected = false;

  const fire = () => {
    frame = null;
    if (!disconnected) cb(presentAnchors(root));
  };
  const schedule = () => {
    if (disconnected || frame !== null) return;
    frame = raf(fire);
  };

  const observer = new MutationObserver(schedule);
  observer.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: [ATTR] });
  cb(presentAnchors(root));

  return () => {
    disconnected = true;
    observer.disconnect();
    if (frame !== null) caf(frame);
    frame = null;
  };
}
